import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  DollarSign, 
  FileText, 
  MapPin, 
  Star,
  Mail,
  MessageSquare,
  LogOut,
  TrendingUp,
  Calendar,
  CheckCircle,
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// For Emergent deployments, frontend and backend share the same domain
const API_URL = window.location.origin;

export const AdminPortal = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [dashboard, setDashboard] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [mailingList, setMailingList] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [centers, setCenters] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [chartPeriod, setChartPeriod] = useState('daily');
  const [showCenterModal, setShowCenterModal] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [centerForm, setCenterForm] = useState({
    name: '',
    location: '',
    description: '',
    treatments: '',
    price_range: '',
    image_url: '',
    contact_phone: '',
    contact_email: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/verify`, { withCredentials: true });
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        loadDashboard();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/login`, credentials, { withCredentials: true });
      setIsAuthenticated(true);
      toast.success('Welcome to Admin Portal', { id: 'admin-login-success' });
      loadDashboard();
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Invalid credentials';
      toast.error(errorMsg, { id: 'admin-login-error' });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      setDashboard(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const [dashboardRes, feedbackRes, mailingRes, consultationsRes, centersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/feedback`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/mailing-list`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/consultations?limit=500`, { withCredentials: true }),
        axios.get(`${API_URL}/api/panchkarma-centers`, { withCredentials: true })
      ]);
      setDashboard(dashboardRes.data);
      setFeedback(feedbackRes.data);
      setMailingList(mailingRes.data);
      setConsultations(consultationsRes.data.consultations || []);
      setCenters(centersRes.data || []);
    } catch (error) {
      console.error('Dashboard load error:', error);
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        setIsAuthenticated(false);
        toast.error('Session expired. Please login again.', { id: 'session-expired' });
      } else {
        toast.error('Failed to load dashboard: ' + (error.response?.data?.detail || error.message), { id: 'dashboard-error' });
      }
    }
  };

  // Helper function to get week number of the year
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Generate chart data based on consultations
  const generateChartData = () => {
    const now = new Date();
    const dataMap = new Map();
    
    if (chartPeriod === 'daily') {
      // Last 14 days
      for (let i = 13; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD for matching
        dataMap.set(dateKey, { name: key, consultations: 0, revenue: 0, previousPeriod: 0, dateKey });
      }
      
      consultations.forEach(c => {
        if (!c.created_at) return;
        const date = new Date(c.created_at);
        const dateKey = date.toISOString().split('T')[0];
        if (dataMap.has(dateKey)) {
          const entry = dataMap.get(dateKey);
          entry.consultations += 1;
          if (c.payment_status === 'paid') {
            const price = c.plan === 'consultation' ? 59 : c.plan === 'verified' ? 29 : 19;
            entry.revenue += price;
          }
        }
      });
      
    } else if (chartPeriod === 'weekly') {
      // Last 12 weeks
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7) - weekStart.getDay());
        const weekNum = getWeekNumber(weekStart);
        const year = weekStart.getFullYear();
        const key = `W${weekNum}`;
        const dateKey = `${year}-W${weekNum}`;
        dataMap.set(dateKey, { name: key, consultations: 0, revenue: 0, previousPeriod: 0, dateKey });
      }
      
      consultations.forEach(c => {
        if (!c.created_at) return;
        const date = new Date(c.created_at);
        const weekNum = getWeekNumber(date);
        const year = date.getFullYear();
        const dateKey = `${year}-W${weekNum}`;
        if (dataMap.has(dateKey)) {
          const entry = dataMap.get(dateKey);
          entry.consultations += 1;
          if (c.payment_status === 'paid') {
            const price = c.plan === 'consultation' ? 59 : c.plan === 'verified' ? 29 : 19;
            entry.revenue += price;
          }
        }
      });
      
    } else {
      // Monthly - last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        dataMap.set(dateKey, { name: key, consultations: 0, revenue: 0, previousPeriod: 0, dateKey });
      }
      
      consultations.forEach(c => {
        if (!c.created_at) return;
        const date = new Date(c.created_at);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (dataMap.has(dateKey)) {
          const entry = dataMap.get(dateKey);
          entry.consultations += 1;
          if (c.payment_status === 'paid') {
            const price = c.plan === 'consultation' ? 59 : c.plan === 'verified' ? 29 : 19;
            entry.revenue += price;
          }
        }
      });
    }
    
    // Convert map to array and add simulated previous period data
    const result = Array.from(dataMap.values());
    result.forEach((entry, i) => {
      // Simulate previous period as 70-110% of current
      entry.previousPeriod = Math.floor(entry.revenue * (0.7 + Math.random() * 0.4));
    });
    
    return result;
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construct full URL
      const imageUrl = `${API_URL}${response.data.url}`;
      setCenterForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Center management functions
  const openCenterModal = (center = null) => {
    if (center) {
      setEditingCenter(center);
      setCenterForm({
        name: center.name || '',
        location: center.location || '',
        description: center.description || '',
        treatments: (center.treatments || []).join(', '),
        price_range: center.price_range || '',
        image_url: center.image_url || '',
        contact_phone: center.contact_phone || '',
        contact_email: center.contact_email || ''
      });
    } else {
      setEditingCenter(null);
      setCenterForm({
        name: '',
        location: '',
        description: '',
        treatments: '',
        price_range: '',
        image_url: '',
        contact_phone: '',
        contact_email: ''
      });
    }
    setShowCenterModal(true);
  };

  const handleSaveCenter = async () => {
    try {
      const centerData = {
        ...centerForm,
        treatments: centerForm.treatments.split(',').map(t => t.trim()).filter(t => t)
      };
      
      if (editingCenter) {
        await axios.put(`${API_URL}/api/admin/panchkarma-centers/${editingCenter.center_id}`, centerData, { withCredentials: true });
        toast.success('Center updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/panchkarma-centers`, centerData, { withCredentials: true });
        toast.success('Center added successfully');
      }
      
      setShowCenterModal(false);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save center');
    }
  };

  const handleDeleteCenter = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this center?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/panchkarma-centers/${centerId}`, { withCredentials: true });
      toast.success('Center deleted successfully');
      loadDashboard();
    } catch (error) {
      toast.error('Failed to delete center');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200" data-testid="admin-login-page">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
              alt="Logo" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Admin Portal
            </h1>
            <p className="text-gray-500 mt-2">Sign in to access the dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                placeholder="Enter username"
                className="mt-1"
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                placeholder="Enter password"
                className="mt-1"
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loginLoading}
              data-testid="admin-login-btn"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const chartData = generateChartData();

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
              alt="Logo" 
              className="h-10"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Ayurveda Consult</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {dashboard && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-green-600">${dashboard.revenue.total_revenue}</p>
                      {dashboard.revenue.total_revenue_change > 0 && (
                        <sup className="text-xs text-green-600 font-semibold bg-green-100 px-1.5 py-0.5 rounded-full">
                          +${dashboard.revenue.total_revenue_change}
                        </sup>
                      )}
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Paid Consultations</p>
                    <p className="text-2xl font-bold text-blue-600">{dashboard.overview.paid_consultations}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Consultations</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-indigo-600">{dashboard.overview.total_consultations}</p>
                      {dashboard.overview.total_consultations_change > 0 && (
                        <sup className="text-xs text-green-600 font-semibold bg-green-100 px-1.5 py-0.5 rounded-full">
                          +{dashboard.overview.total_consultations_change}
                        </sup>
                      )}
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unique Users</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-purple-600">{dashboard.overview.total_users}</p>
                      {dashboard.overview.total_users_change > 0 && (
                        <sup className="text-xs text-green-600 font-semibold bg-green-100 px-1.5 py-0.5 rounded-full">
                          +{dashboard.overview.total_users_change}
                        </sup>
                      )}
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Paid Consults</p>
                <p className="text-lg font-bold text-blue-600">{dashboard.overview.paid_consultations}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Pending Payments</p>
                <p className="text-lg font-bold text-orange-600">{dashboard.overview.pending_payments}</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Bookings</p>
                <div className="flex items-center gap-1">
                  <p className="text-lg font-bold text-teal-600">{dashboard.overview.total_bookings}</p>
                  {dashboard.overview.total_bookings_change > 0 && (
                    <sup className="text-xs text-green-600 font-semibold">+{dashboard.overview.total_bookings_change}</sup>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Avg Rating</p>
                <p className="text-lg font-bold text-yellow-600">{dashboard.feedback_stats.average_rating || 'N/A'}</p>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-white border border-gray-200 p-1 rounded-lg flex-wrap h-auto">
                <TabsTrigger value="overview" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="analytics" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="consultations" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  Consultations
                </TabsTrigger>
                <TabsTrigger value="feedback" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Feedback
                </TabsTrigger>
                <TabsTrigger value="mailing" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <Mail className="w-4 h-4 mr-2" />
                  Mailing List
                </TabsTrigger>
                <TabsTrigger value="centers" className="data-[state=active]:bg-[hsl(22,70%,44%)] data-[state=active]:text-white">
                  <MapPin className="w-4 h-4 mr-2" />
                  Centers
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Plan Breakdown */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-800 mb-4">Plan Breakdown</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">AI Assessment ($19)</span>
                        <span className="font-semibold">{dashboard.plan_breakdown.basic.count} (${dashboard.plan_breakdown.basic.revenue})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Doctor-Reviewed ($29)</span>
                        <span className="font-semibold">{dashboard.plan_breakdown.verified.count} (${dashboard.plan_breakdown.verified.revenue})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">1-on-1 Consultation ($59)</span>
                        <span className="font-semibold">{dashboard.plan_breakdown.consultation.count} (${dashboard.plan_breakdown.consultation.revenue})</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-800 mb-4">Quick Stats</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Panchkarma Bookings</span>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold">{dashboard.overview.total_bookings}</span>
                          {dashboard.overview.total_bookings_change > 0 && (
                            <sup className="text-xs text-green-600 font-semibold">+{dashboard.overview.total_bookings_change}</sup>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Active Centers</span>
                        <span className="font-semibold">{dashboard.overview.total_centers}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Pending Payments</span>
                        <span className="font-semibold text-orange-600">{dashboard.overview.pending_payments}</span>
                      </div>
                    </div>
                  </div>

                  {/* Feedback Stats */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-800 mb-4">Feedback Stats</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Total Feedback</span>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold">{dashboard.feedback_stats.total_feedback}</span>
                          {dashboard.overview.total_feedback_change > 0 && (
                            <sup className="text-xs text-green-600 font-semibold">+{dashboard.overview.total_feedback_change}</sup>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Mailing List Signups</span>
                        <span className="font-semibold text-green-600">{dashboard.feedback_stats.mailing_list_signups}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Testimonial Permissions</span>
                        <span className="font-semibold text-blue-600">{dashboard.feedback_stats.testimonial_permissions}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-800 mb-4">Recent Consultations</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Client</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Plan</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.recent_consultations.map((c, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-gray-800">{c.client_name || 'N/A'}</p>
                                <p className="text-sm text-gray-500">{c.client_email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                c.plan === 'consultation' ? 'bg-purple-100 text-purple-700' :
                                c.plan === 'verified' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {c.plan === 'consultation' ? '1-on-1' : c.plan === 'verified' ? 'Verified' : 'Basic'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                c.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {c.payment_status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">
                              {c.created_at ? new Date(c.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-gray-800">Sales & Consultations Trend</h3>
                    <Select value={chartPeriod} onValueChange={setChartPeriod}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily (14 days)</SelectItem>
                        <SelectItem value="weekly">Weekly (12 weeks)</SelectItem>
                        <SelectItem value="monthly">Monthly (12 months)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" stroke="#888" fontSize={12} />
                        <YAxis stroke="#888" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          name="Revenue ($)" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          dot={{ fill: '#22c55e', strokeWidth: 2 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="previousPeriod" 
                          name="Previous Period ($)" 
                          stroke="#94a3b8" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={{ fill: '#94a3b8', strokeWidth: 2 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="consultations" 
                          name="Consultations" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        ${chartData.reduce((sum, d) => sum + d.revenue, 0)}
                      </p>
                      <p className="text-sm text-gray-500">Total Period Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {chartData.reduce((sum, d) => sum + d.consultations, 0)}
                      </p>
                      <p className="text-sm text-gray-500">Total Consultations</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        ${chartData.length ? Math.round(chartData.reduce((sum, d) => sum + d.revenue, 0) / chartData.length) : 0}
                      </p>
                      <p className="text-sm text-gray-500">Avg per Period</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Consultations Tab */}
              <TabsContent value="consultations">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-800 mb-4">All Consultations ({consultations.length})</h3>
                  <div className="space-y-4">
                    {consultations.map((c, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                        {/* Header Row */}
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-3 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{c.consultation_id?.slice(-8)}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              c.plan === 'consultation' ? 'bg-purple-100 text-purple-700' :
                              c.plan === 'verified' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {c.plan === 'consultation' ? '1-on-1 ($59)' : c.plan === 'verified' ? 'Verified ($29)' : 'Basic ($19)'}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              c.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {c.payment_status}
                            </span>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {c.status}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {c.created_at ? new Date(c.created_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        
                        {/* Client Details */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Client Name</p>
                            <p className="text-sm font-medium text-gray-800">{c.client_name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Email</p>
                            <p className="text-sm text-gray-700">{c.client_email || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Phone</p>
                            <p className="text-sm text-gray-700">{c.client_phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Age / Gender</p>
                            <p className="text-sm text-gray-700">{c.client_age || 'N/A'} / {c.client_gender || 'N/A'}</p>
                          </div>
                        </div>
                        
                        {/* Symptoms */}
                        {c.symptoms && c.symptoms.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Health Concerns</p>
                            <div className="flex flex-wrap gap-1">
                              {c.symptoms.map((s, idx) => (
                                <span key={idx} className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                                  {s.name || s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Detailed Description */}
                        {c.detailed_description && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Detailed Description</p>
                            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{c.detailed_description}</p>
                          </div>
                        )}
                        
                        {/* Lifestyle Answers */}
                        {c.lifestyle_answers && Object.keys(c.lifestyle_answers).length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1">Lifestyle Info</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(c.lifestyle_answers).map(([key, value]) => (
                                <span key={key} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                  {key.replace(/_/g, ' ')}: {value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Diagnosis Summary */}
                        {c.diagnosis && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-green-600 mb-1">✓ Assessment Completed</p>
                            <p className="text-xs text-gray-500">
                              Dosha: {c.diagnosis.dosha_imbalance || 'N/A'} | 
                              Report Sent: {c.report_sent ? 'Yes' : 'No'}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Feedback Tab */}
              <TabsContent value="feedback">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-800 mb-4">Customer Feedback ({feedback.length})</h3>
                  {feedback.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No feedback received yet</p>
                  ) : (
                    <div className="space-y-4">
                      {feedback.map((f, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-medium text-gray-800">{f.user_name}</p>
                              <p className="text-sm text-gray-500">{f.user_email}</p>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                {[1,2,3,4,5].map(star => (
                                  <Star key={star} className={`w-4 h-4 ${star <= f.overall_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{new Date(f.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-green-700 mb-1">What they loved:</p>
                              <p className="text-sm text-gray-600">{f.what_loved}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-orange-700 mb-1">Improvements suggested:</p>
                              <p className="text-sm text-gray-600">{f.improvements}</p>
                            </div>
                          </div>
                          <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100">
                            <span className={`text-xs px-2 py-1 rounded ${f.would_recommend ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {f.would_recommend ? '✓ Would Recommend' : '✗ Would Not Recommend'}
                            </span>
                            {f.join_mailing_list && (
                              <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">✓ Joined Mailing List</span>
                            )}
                            {f.testimonial_permission && (
                              <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">✓ Testimonial OK</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Mailing List Tab */}
              <TabsContent value="mailing">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-800 mb-4">Mailing List ({mailingList.length} subscribers)</h3>
                  {mailingList.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No subscribers yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mailingList.map((m, i) => (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{m.user_name}</td>
                              <td className="py-3 px-4">{m.user_email}</td>
                              <td className="py-3 px-4 text-sm text-gray-500">
                                {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Centers Tab */}
              <TabsContent value="centers">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-gray-800">Panchkarma Centers ({centers.length})</h3>
                    <Button onClick={() => openCenterModal()} className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Center
                    </Button>
                  </div>
                  
                  {centers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No centers added yet</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {centers.map((center) => (
                        <div key={center.center_id} className="border border-gray-200 rounded-lg p-4">
                          <div className="h-32 mb-4 rounded-lg overflow-hidden bg-gray-100">
                            <img 
                              src={center.image_url || 'https://images.unsplash.com/photo-1711714956204-e1d84e4d8879'} 
                              alt={center.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <h4 className="font-semibold text-gray-800 mb-1">{center.name}</h4>
                          <p className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                            <MapPin className="w-3 h-3" />
                            {center.location}
                          </p>
                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">{center.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[hsl(22,70%,44%)]">{center.price_range}</span>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => openCenterModal(center)}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteCenter(center.center_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      {/* Center Modal */}
      {showCenterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                {editingCenter ? 'Edit Center' : 'Add New Center'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCenterModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={centerForm.name}
                  onChange={(e) => setCenterForm({...centerForm, name: e.target.value})}
                  placeholder="Center name"
                />
              </div>
              <div>
                <Label>Location *</Label>
                <Input
                  value={centerForm.location}
                  onChange={(e) => setCenterForm({...centerForm, location: e.target.value})}
                  placeholder="City, Country"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={centerForm.description}
                  onChange={(e) => setCenterForm({...centerForm, description: e.target.value})}
                  placeholder="Brief description of the center"
                  rows={3}
                />
              </div>
              <div>
                <Label>Treatments (comma separated)</Label>
                <Input
                  value={centerForm.treatments}
                  onChange={(e) => setCenterForm({...centerForm, treatments: e.target.value})}
                  placeholder="Abhyanga, Shirodhara, Panchakarma"
                />
              </div>
              <div>
                <Label>Price Range</Label>
                <Input
                  value={centerForm.price_range}
                  onChange={(e) => setCenterForm({...centerForm, price_range: e.target.value})}
                  placeholder="$500 - $2000"
                />
              </div>
              <div>
                <Label>Center Image</Label>
                <div className="mt-2 space-y-3">
                  {/* Image Preview */}
                  {centerForm.image_url && (
                    <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={centerForm.image_url} 
                        alt="Center preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <div className={`flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[hsl(22,70%,44%)] hover:bg-orange-50 transition-colors ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {uploadingImage ? (
                          <>
                            <div className="w-4 h-4 border-2 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600">Upload Image</span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                  
                  {/* Or use URL */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-500">or enter URL</span>
                    </div>
                  </div>
                  
                  <Input
                    value={centerForm.image_url}
                    onChange={(e) => setCenterForm({...centerForm, image_url: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={centerForm.contact_phone}
                    onChange={(e) => setCenterForm({...centerForm, contact_phone: e.target.value})}
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    value={centerForm.contact_email}
                    onChange={(e) => setCenterForm({...centerForm, contact_email: e.target.value})}
                    placeholder="email@center.com"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCenterModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCenter} className="btn-primary">
                <Save className="w-4 h-4 mr-2" />
                {editingCenter ? 'Update Center' : 'Add Center'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
